export type ConnectivityStatusFilter = 'active' | 'inactive';

export interface ConnectivityListContext {
  groupId: number | null;
  page: number;
  pageSize: number;
  search: string;
  status: ConnectivityStatusFilter | null;
  focus: number | null;
}

export interface QueryParamReader {
  get(name: string): string | null;
}

export const CLIENT_CONNECTIVITY_GROUP_PARAM = 'group';
export const CLIENT_CONNECTIVITY_PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_CONNECTIVITY_PAGE = 1;
export const DEFAULT_CONNECTIVITY_PAGE_SIZE = 50;

export function parseClientListContext(reader: QueryParamReader): ConnectivityListContext {
  return parseListContext(reader, CLIENT_CONNECTIVITY_GROUP_PARAM, CLIENT_CONNECTIVITY_PAGE_SIZES);
}

export function serializeClientListContext(
  context: ConnectivityListContext,
): Record<string, string | number> {
  return serializeListContext(context, CLIENT_CONNECTIVITY_GROUP_PARAM);
}

export function canonicalClientListQuery(
  params: Record<string, unknown> | null | undefined,
): Record<string, string | number> {
  return serializeClientListContext(parseClientListContext(readerFromParams(params)));
}

export function clientListContextsEqual(
  left: ConnectivityListContext,
  right: ConnectivityListContext,
): boolean {
  return (
    left.groupId === right.groupId &&
    left.page === right.page &&
    left.pageSize === right.pageSize &&
    left.search === right.search &&
    left.status === right.status &&
    left.focus === right.focus
  );
}

export function interfaceIdsForStatus(
  summary: { filters?: Record<string, { institutionInterfaceIds?: unknown }> } | null | undefined,
  status: ConnectivityStatusFilter,
): number[] {
  const filters = summary?.filters;
  if (!filters) return [];

  const readIds = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
  const raw =
    status === 'inactive'
      ? [
          ...readIds(filters['inactive']?.institutionInterfaceIds),
          ...readIds(filters['unknown']?.institutionInterfaceIds),
        ]
      : readIds(filters['active']?.institutionInterfaceIds);

  const ids: number[] = [];
  raw.forEach((value) => {
    const id = Number(value);
    if (Number.isInteger(id) && id > 0 && !ids.includes(id)) {
      ids.push(id);
    }
  });
  return ids;
}

export function readerFromParams(
  params: Record<string, unknown> | null | undefined,
): QueryParamReader {
  const source = params ?? {};
  return {
    get(name: string): string | null {
      if (!Object.prototype.hasOwnProperty.call(source, name)) return null;
      const value = source[name];
      if (value == null) return null;
      if (Array.isArray(value)) {
        const first = value[0];
        return first == null ? null : String(first);
      }
      return String(value);
    },
  };
}

function parseListContext(
  reader: QueryParamReader,
  groupParam: string,
  pageSizes: readonly number[],
): ConnectivityListContext {
  const searchRaw = reader.get('search');
  const statusRaw = reader.get('status');

  return {
    groupId: readPositiveInt(reader.get(groupParam)),
    page: readPositiveInt(reader.get('page')) ?? DEFAULT_CONNECTIVITY_PAGE,
    pageSize: readPageSize(reader.get('pageSize'), pageSizes),
    search: searchRaw == null ? '' : searchRaw,
    status: statusRaw === 'active' || statusRaw === 'inactive' ? statusRaw : null,
    focus: readPositiveInt(reader.get('focus')) ?? readPositiveInt(reader.get('selected')),
  };
}

function serializeListContext(
  context: ConnectivityListContext,
  groupParam: string,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (context.groupId != null) params[groupParam] = context.groupId;
  if (context.page !== DEFAULT_CONNECTIVITY_PAGE) params['page'] = context.page;
  if (context.pageSize !== DEFAULT_CONNECTIVITY_PAGE_SIZE) params['pageSize'] = context.pageSize;
  if (context.search !== '') params['search'] = context.search;
  if (context.status) params['status'] = context.status;
  if (context.focus != null) params['focus'] = context.focus;
  return params;
}

function readPageSize(raw: string | null, pageSizes: readonly number[]): number {
  const pageSize = readPositiveInt(raw);
  if (pageSize != null && pageSizes.includes(pageSize)) return pageSize;
  return DEFAULT_CONNECTIVITY_PAGE_SIZE;
}

function readPositiveInt(raw: string | null): number | null {
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value <= 0) return null;
  return value;
}
